import { Children } from 'react'
import { isNumber } from '@mote/helpers/assertion'
import { __DEV__ } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'
import type { SystemProp, Theme } from '@xstyled/styled-components'
import styled, { css } from '@xstyled/styled-components'
import type { HTMLXStyledProps } from '../../system'
import { forwardRef } from '../../system'

export interface AspectRatioProps extends HTMLXStyledProps<'div'> {
  ratio?: SystemProp<number, Theme>
}

export const AspectRatio = forwardRef<AspectRatioProps, 'div'>(function (
  props,
  ref
) {
  const { ratio = 4 / 3, children, className, ...rest } = props
  const child = Children.only(children)
  const _className = mergeClassnames('mote-aspect-ratio', className)

  return (
    <Root ref={ref} className={_className} $ratio={ratio} {...rest}>
      {child}
    </Root>
  )
})

if (__DEV__) {
  AspectRatio.displayName = 'AspectRatio'
}

const Root = styled.divBox<{ $ratio: SystemProp<number, Theme> }>`
  position: relative;

  &::before {
    height: 0;
    content: '';
    display: block;

    ${({ $ratio }) => {
      if (isNumber($ratio)) {
        return css`
          padding-bottom: ${(1 / ($ratio as number)) * 100}%;
        `
      }

      return Object.entries($ratio).map(([key, value]) => {
        if (['_', 'xs'].includes(key)) {
          return css`
            @media (min-width: xs) {
              padding-bottom: ${(1 / value) * 100}%;
            }
          `
        }

        return css`
          @media (min-width: ${key}) {
            padding-bottom: ${(1 / value) * 100}%;
          }
        `
      })
    }};
  }

  & > *:not(style) {
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  & > img,
  & > video {
    object-fit: cover;
  }
`
